import { deleteCookie, setCookie } from 'cookies-next'
import * as React from 'react'

import useLocale from '@/hooks/use-locale'

import { SESSION_ID_HEADER } from '@/constant/app'
import authService from '@/services/auth.service'
import { baseService } from '@/services/base.service'
import userService from '@/services/user.service'
import { isInvalidUserApiError, isPublicAuthPath } from '@/utils/auth-api-error'
import { shouldUseJwtAuth } from '@/utils/tenant.utils'

import type { UserProfile } from '@/types/user.type'

export type LoginOptions = {
  /** Cookie max-age in seconds (e.g. from community sign-in expires_in) */
  sessionMaxAgeSeconds?: number
  /** JWT access token (issued by backend on non-fsds origins) */
  accessToken?: string
  /** JWT refresh token (issued by backend on non-fsds origins) */
  refreshToken?: string
  /** Backend's expiry hints (e.g. "1d", "30d"); fed to BaseService cookie maxAge */
  accessTokenExpiresIn?: string
  refreshTokenExpiresIn?: string
}

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  login: (userData: UserProfile | undefined, sessionId?: string, options?: LoginOptions) => void
  logout: () => void
  user?: UserProfile
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: React.ReactNode
}

// Add action types for reducer
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: UserProfile | undefined }
  | { type: 'LOGOUT' }

interface AuthState {
  user: UserProfile | undefined
  isLoading: boolean
}

const initialState: AuthState = {
  user: undefined,
  isLoading: true,
}

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    case 'SET_USER':
      return { ...state, user: action.payload, isLoading: false }
    case 'LOGOUT':
      return { ...state, user: undefined, isLoading: false }
    default:
      return state
  }
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = React.useReducer(authReducer, initialState)
  const { lang } = useLocale()
  const logout = React.useCallback(async () => {
    try {
      await authService.logout()
      deleteCookie(SESSION_ID_HEADER)
      baseService.clearSessionId()
      baseService.clearAuthTokens()
      dispatch({ type: 'LOGOUT' })
      window.location.href = lang === 'vi' ? '/' : '/en'
    } catch (error) {
      // Still logout user on frontend even if API call fails
      baseService.clearSessionId()
      baseService.clearAuthTokens()
      dispatch({ type: 'LOGOUT' })
      window.location.href = lang === 'vi' ? '/' : '/en'
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const login = React.useCallback(
    async (userData: UserProfile | undefined, sessionId?: string, options?: LoginOptions) => {
      if (!userData) return
      try {
        // JWT mode (non-fsds hosts): persist access + refresh tokens. BaseService
        // attaches them as Authorization on every subsequent request.
        if (options?.accessToken && options?.refreshToken) {
          baseService.setAuthTokens({
            accessToken: options.accessToken,
            refreshToken: options.refreshToken,
            accessTokenExpiresIn: options.accessTokenExpiresIn,
            refreshTokenExpiresIn: options.refreshTokenExpiresIn,
          })
        }

        // Cookie mode (fsds hosts): set the first-party SESSION_ID cookie so
        // SSR middleware can also see the session. Skipped on non-fsds since
        // JWT supersedes the session cookie there.
        if (sessionId && !shouldUseJwtAuth()) {
          const maxAge = options?.sessionMaxAgeSeconds ?? 60 * 60 * 24 * 7
          const cookieOptions: {
            path: string
            secure: boolean
            maxAge: number
            domain?: string
          } = {
            path: '/',
            secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
            maxAge,
          }
          if (typeof window !== 'undefined') {
            const hostname = window.location.hostname
            if (hostname === 'fullstackdatascience.com' || hostname.endsWith('.fullstackdatascience.com')) {
              cookieOptions.domain = '.fullstackdatascience.com'
            }
          }
          setCookie(SESSION_ID_HEADER, sessionId, {
            ...cookieOptions,
          })
          baseService.setSessionId(sessionId)
        }
        dispatch({ type: 'SET_USER', payload: userData })
      } catch {
        dispatch({ type: 'LOGOUT' })
      }
    },
    []
  )

  const getCurrentUser = React.useCallback(async () => {
    try {
      if (typeof window === 'undefined') return

      // JWT mode: skip profile fetch when no access token in first-party cookies.
      if (shouldUseJwtAuth() && !baseService.getAccessToken()) {
        dispatch({ type: 'LOGOUT' })
        return
      }

      dispatch({ type: 'SET_LOADING', payload: true })

      // Session mode: browser sends session via header and/or withCredentials (may be HttpOnly).
      try {
        const currentUser = await userService.getProfile()
        dispatch({ type: 'SET_USER', payload: currentUser })
      } catch (error) {
        if (isInvalidUserApiError(error)) {
          deleteCookie(SESSION_ID_HEADER)
          baseService.clearSessionId()
          baseService.clearAuthTokens()
        }
        dispatch({ type: 'LOGOUT' })
      }
    } catch {
      dispatch({ type: 'LOGOUT' })
    }
  }, [])

  React.useEffect(() => {
    if (typeof window !== 'undefined' && isPublicAuthPath(window.location.pathname)) {
      dispatch({ type: 'LOGOUT' })
      return
    }
    getCurrentUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!state.user,
        isLoading: state.isLoading,
        login,
        logout,
        user: state.user,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = React.useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
